import { useEffect, useRef, useState } from 'react';
import { IconGlyph } from '../ui/primitives/index.js';
import { detectVoiceCapability, voiceCapabilityMessage } from './voice-capability.js';

interface SpeechRecognitionResultLike {
  isFinal: boolean;
  0: { transcript: string };
}

interface SpeechRecognitionEventLike {
  resultIndex: number;
  results: ArrayLike<SpeechRecognitionResultLike>;
}

interface SpeechRecognitionErrorEventLike {
  error: string;
}

interface SpeechRecognitionLike {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  onend: (() => void) | null;
  onerror: ((event: SpeechRecognitionErrorEventLike) => void) | null;
  onresult: ((event: SpeechRecognitionEventLike) => void) | null;
  start(): void;
  stop(): void;
  abort(): void;
}

type SpeechRecognitionConstructor = new () => SpeechRecognitionLike;

declare global {
  interface Window {
    SpeechRecognition?: SpeechRecognitionConstructor;
    webkitSpeechRecognition?: SpeechRecognitionConstructor;
  }
}

export function VoiceJournalRecorder(props: { disabled?: boolean; onCapture: (file: File, transcript: string) => Promise<void> }) {
  const [status, setStatus] = useState<'idle' | 'recording' | 'finishing' | 'error'>('idle');
  const [elapsed, setElapsed] = useState(0);
  const [finalText, setFinalText] = useState('');
  const [interimText, setInterimText] = useState('');
  const [error, setError] = useState('');
  const recorderRef = useRef<MediaRecorder | null>(null);
  const recognitionRef = useRef<SpeechRecognitionLike | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const finalTextRef = useRef('');
  const interimTextRef = useRef('');

  useEffect(() => {
    if (status !== 'recording') return;
    const timer = window.setInterval(() => setElapsed((value) => value + 1), 1000);
    return () => window.clearInterval(timer);
  }, [status]);

  useEffect(() => () => cleanup(true), []);

  if (status === 'recording' || status === 'finishing') {
    return (
      <div className="mira-voice-recorder" aria-live="polite">
        <strong><i aria-hidden="true" /> Voice note {formatElapsed(elapsed)}</strong>
        <p>{finalText}{interimText ? <em>{interimText}</em> : null}</p>
        <button disabled={status === 'finishing'} onClick={() => void stop()} type="button">Stop & check text</button>
        <button disabled={status === 'finishing'} onClick={() => cancel()} type="button">Cancel</button>
      </div>
    );
  }

  return (
    <>
      <button disabled={props.disabled} onClick={() => void start()} type="button"><IconGlyph value="◉" /> Voice</button>
      {error ? <span className="mira-voice-error" role="alert">{error}</span> : null}
    </>
  );

  async function start() {
    const Recognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    const capability = detectVoiceCapability({
      isSecureContext: window.isSecureContext,
      hasMediaDevices: Boolean(navigator.mediaDevices?.getUserMedia),
      hasMediaRecorder: typeof MediaRecorder !== 'undefined',
      hasSpeechRecognition: Boolean(Recognition),
    });
    if (capability !== 'supported' || !Recognition) {
      setStatus('error');
      setError(voiceCapabilityMessage(capability));
      return;
    }
    try {
      setError('');
      setElapsed(0);
      setFinalText('');
      setInterimText('');
      finalTextRef.current = '';
      interimTextRef.current = '';
      chunksRef.current = [];
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mimeType = preferredAudioMimeType();
      const recorder = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream);
      const recognition = new Recognition();
      recorderRef.current = recorder;
      recognitionRef.current = recognition;
      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) chunksRef.current.push(event.data);
      };
      recognition.lang = 'en-US';
      recognition.continuous = true;
      recognition.interimResults = true;
      recognition.onresult = (event) => {
        let interim = '';
        let committed = '';
        for (let index = event.resultIndex; index < event.results.length; index += 1) {
          const result = event.results[index];
          const transcript = result?.[0]?.transcript || '';
          if (result?.isFinal) committed += transcript;
          else interim += transcript;
        }
        if (committed) {
          finalTextRef.current = `${finalTextRef.current}${committed}`;
          setFinalText(finalTextRef.current);
        }
        interimTextRef.current = interim;
        setInterimText(interim);
      };
      recognition.onerror = (event) => {
        if (event.error === 'aborted') return;
        cleanup(true);
        setStatus('error');
        setError(event.error === 'not-allowed' || event.error === 'service-not-allowed'
          ? 'Microphone or speech-recognition permission missing. Allow it in browser settings and try again.'
          : 'Transcription was interrupted. Please record again.');
      };
      recorder.start(500);
      recognition.start();
      setStatus('recording');
    } catch (startError) {
      cleanup(true);
      setStatus('error');
      setError(startError instanceof DOMException && startError.name === 'NotAllowedError'
        ? 'Microphone permission missing. Allow it in browser settings and try again.'
        : 'Recording could not start. Check your microphone and try again.');
    }
  }

  async function stop() {
    const recorder = recorderRef.current;
    if (!recorder || status !== 'recording') return;
    setStatus('finishing');
    recognitionRef.current?.stop();
    const blob = await new Promise<Blob>((resolve) => {
      recorder.addEventListener('stop', () => resolve(new Blob(chunksRef.current, { type: normalizedRecorderMime(recorder.mimeType) })), { once: true });
      recorder.stop();
    });
    const transcript = `${finalTextRef.current} ${interimTextRef.current}`.trim();
    cleanup(false);
    if (!blob.size || !transcript) {
      setStatus('error');
      setError(!blob.size ? 'No sound was captured. Please record again.' : 'No text was recognized. Record again or type instead.');
      return;
    }
    const mimeType = normalizedRecorderMime(blob.type);
    const file = new File([blob], `voice-note-${new Date().toISOString().replace(/[:.]/gu, '-')}.${audioExtension(mimeType)}`, { type: mimeType });
    try {
      await props.onCapture(file, transcript);
      setStatus('idle');
      setFinalText('');
      setInterimText('');
    } catch {
      setStatus('error');
      setError('The voice note could not be processed. Please try again.');
    }
  }

  function cancel() {
    cleanup(true);
    setStatus('idle');
    setError('');
    setFinalText('');
    setInterimText('');
  }

  function cleanup(abortRecognition: boolean) {
    try {
      if (abortRecognition) recognitionRef.current?.abort();
      else recognitionRef.current?.stop();
    } catch {
      // The recognition object may already be stopped by the browser.
    }
    try {
      if (recorderRef.current?.state === 'recording') recorderRef.current.stop();
    } catch {
      // The recorder may already have emitted its final stop event.
    }
    streamRef.current?.getTracks().forEach((track) => track.stop());
    recorderRef.current = null;
    recognitionRef.current = null;
    streamRef.current = null;
  }
}

function preferredAudioMimeType(): string {
  return ['audio/webm;codecs=opus', 'audio/mp4', 'audio/ogg;codecs=opus']
    .find((mimeType) => MediaRecorder.isTypeSupported(mimeType)) || '';
}

function normalizedRecorderMime(mimeType: string): string {
  const base = mimeType.split(';', 1)[0]?.toLowerCase();
  return base === 'audio/mp4' || base === 'audio/ogg' ? base : 'audio/webm';
}

function audioExtension(mimeType: string): string {
  if (mimeType === 'audio/mp4') return 'm4a';
  if (mimeType === 'audio/ogg') return 'ogg';
  return 'webm';
}

function formatElapsed(seconds: number): string {
  return `${String(Math.floor(seconds / 60)).padStart(2, '0')}:${String(seconds % 60).padStart(2, '0')}`;
}
